import { Navigate, Outlet } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { isTokenExpired } from "@/auth/jwt";
import { BrandedLoader } from "@/components/shared/BrandedLoader";
import { DASHBOARD_BY_SCOPE } from "./viewAs";

export function PublicRoute() {
  const { token, user, restoring } = useAppSelector((state) => state.auth);

  if (restoring) return <BrandedLoader variant="fullscreen" label="Restoring your session…" />;

  // Mirrors ProtectedRoute's own gate: an expired access token must never
  // be treated as "logged in" here either. Redux only clears a stale
  // token/user pair on a fresh page load (authSlice's readStoredSession) —
  // if the 15-minute access token expires while the tab stays open, these
  // stay truthy-but-stale until something reloads the page. Without this
  // check, once that happens: ProtectedRoute sees isTokenExpired and sends
  // the user to /login, PublicRoute (guarding /login) sees a still-truthy
  // token+user and sends them straight back, ProtectedRoute bounces them
  // again — an infinite Navigate ping-pong ("Maximum update depth
  // exceeded") on the very next client-side navigation after expiry.
  // A first-login user isn't "logged in" for routing purposes yet — they
  // still owe LoginPage's forced ChangePasswordModal. Redirecting them out
  // of /login here would unmount that modal before it can ever be seen.
  const loggedIn = !!token && !!user && !user.isFirstLogin && !isTokenExpired(token);

  // Only redirect away from /login when we actually know where to send the
  // user. A scope with no dashboard yet (see DASHBOARD_BY_SCOPE) must fall
  // through to the login page itself, never navigate back to it — that
  // would loop forever since PublicRoute guards /login.
  const target = loggedIn ? DASHBOARD_BY_SCOPE[user.role.scope] : undefined;
  if (target) {
    return <Navigate to={target} replace />;
  }

  return <Outlet />;
}
